import type { CmsPage } from '../../../cms/types'
import type { Route } from '../../types'
import type { ShopLocale } from '../../../i18n/shopLocale'
import { buildPageBreadcrumbs, normalizeSlugPath, trimText } from '../../seoHelpers'
import { DEFAULT_DESCRIPTION } from '../constants'
import { buildDefaultSeoAlternates, dedupeSeoAlternates } from '../localePolicy'
import type { ShopSeoData } from '../types'
import { resolveLocaleFallback } from './shared'

export const buildHomeSeo = ({
  pageData,
  siteName,
  activeLocale,
}: {
  pageData: CmsPage
  siteName: string
  activeLocale?: ShopLocale
}): ShopSeoData => {
  const title = trimText(pageData.metaTitle || pageData.title || siteName, 70) || siteName
  const description =
    trimText(pageData.metaDescription || pageData.excerpt || DEFAULT_DESCRIPTION, 160) ||
    DEFAULT_DESCRIPTION

  const canonicalLocale = pageData.seoCanonicalLocale ?? pageData.locale
  const localeFallback = resolveLocaleFallback({ activeLocale, canonicalLocale })
  const noindex = Boolean(localeFallback)

  return {
    title,
    description,
    canonicalPath: '/',
    canonicalLocale,
    alternates:
      pageData.seoAlternates && pageData.seoAlternates.length
        ? dedupeSeoAlternates(pageData.seoAlternates, '/')
        : buildDefaultSeoAlternates('/'),
    noindex,
    ...(localeFallback ? { localeFallback } : {}),
    ogType: 'website',
    pageType: 'home',
    entityName: pageData.title || siteName,
    breadcrumbs: [{ name: 'Home', path: '/' }],
  }
}

export const buildCmsPageSeo = ({
  route,
  pageData,
  currentPathname,
  siteName,
  activeLocale,
}: {
  route: Route
  pageData: CmsPage
  currentPathname?: string
  siteName: string
  activeLocale?: ShopLocale
}): ShopSeoData => {
  const pagePath =
    route.mode === 'preview'
      ? normalizeSlugPath(currentPathname || `/preview/${encodeURIComponent(route.id)}`)
      : normalizeSlugPath(pageData.slug || (route.mode === 'page' ? route.slug : '/'))
  const title = trimText(pageData.metaTitle || pageData.title || siteName, 70) || siteName
  const description =
    trimText(pageData.metaDescription || pageData.excerpt || DEFAULT_DESCRIPTION, 160) ||
    DEFAULT_DESCRIPTION
  const canonicalLocale = pageData.seoCanonicalLocale ?? pageData.locale
  const localeFallback = resolveLocaleFallback({ activeLocale, canonicalLocale })
  const noindex = route.mode === 'preview' || pageData.status === 'draft' || Boolean(localeFallback)

  return {
    title,
    description,
    canonicalPath: pagePath,
    canonicalLocale,
    alternates:
      pageData.seoAlternates && pageData.seoAlternates.length
        ? dedupeSeoAlternates(pageData.seoAlternates, pagePath)
        : buildDefaultSeoAlternates(pagePath),
    noindex,
    ...(localeFallback ? { localeFallback } : {}),
    ogType: 'website',
    pageType: pagePath === '/' ? 'home' : 'page',
    entityName: pageData.title || siteName,
    breadcrumbs: buildPageBreadcrumbs(pageData.title || 'Pagina', pagePath),
  }
}
